import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiDelete, apiGet, apiPost } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import { CreatePolicyModal } from '../components/CreatePolicyModal'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Skeleton,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

const PAGE_SIZE = 25

interface Resource {
  id: string
  name: string
  type: string
}

interface PolicyConstraints {
  allowed_source_ips?: string[]
  time_restriction?: { start_hour: number; end_hour: number }
}

interface Policy {
  id: string
  tenant_id: string
  resource_id?: string
  tag_group_id?: string
  effect?: string
  allowed_subjects: string[]
  allowed_groups: string[]
  clipboard_allowed: boolean
  constraints?: PolicyConstraints
}

interface PagedResponse<T> {
  items: T[]
  page: number
  page_size: number
  total: number
}

interface GroupItem {
  claim_value: string
  label: string
}

type Mode = 'group' | 'resource' | 'all'

const DIRECT_GROUP_KEY = '__direct__'

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') p.set(k, String(v))
  })
  return p.toString()
}

function constraintLabel(c?: PolicyConstraints): string {
  if (!c) return '-'
  const parts: string[] = []
  if (c.allowed_source_ips && c.allowed_source_ips.length > 0) {
    parts.push(`IP ${c.allowed_source_ips.join(', ')}`)
  }
  if (c.time_restriction) {
    const pad = (n: number) => String(n).padStart(2, '0')
    parts.push(`${pad(c.time_restriction.start_hour)}:00–${pad(c.time_restriction.end_hour)}:00 UTC`)
  }
  return parts.length > 0 ? parts.join(' · ') : '-'
}

export default function AdminPoliciesPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()

  const [resources, setResources] = useState<Resource[]>([])
  const [availableGroups, setAvailableGroups] = useState<GroupItem[]>([])
  const [dirBanner, setDirBanner] = useState(false)

  const [mode, setMode] = useState<Mode>('group')
  const [query, setQuery] = useState('')
  const [activeGroup, setActiveGroup] = useState<string>('')
  const [activeResource, setActiveResource] = useState<string>('')
  const [page, setPage] = useState(1)

  const [listResp, setListResp] = useState<PagedResponse<Policy>>({ items: [], page: 1, page_size: PAGE_SIZE, total: 0 })
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const [facetCounts, setFacetCounts] = useState<Record<string, number>>({})
  const [tagGroupById, setTagGroupById] = useState<Record<string, { name: string }>>({})

  useEffect(() => {
    if (!tenantId) return
    apiGet<{ items: { id: string; name: string }[] }>(`/api/v1/tenants/${tenantId}/tag-groups?limit=500`)
      .then((d) => {
        const map: Record<string, { name: string }> = {}
        for (const g of d.items ?? []) map[g.id] = { name: g.name }
        setTagGroupById(map)
      })
      .catch(() => setTagGroupById({}))
  }, [tenantId])

  const [showForm, setShowForm] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null)

  const resourceById = useMemo(() => Object.fromEntries(resources.map((r) => [r.id, r])), [resources])
  const groupLabel = useCallback(
    (claim: string) => availableGroups.find((g) => g.claim_value === claim)?.label ?? claim,
    [availableGroups],
  )

  // ---- metadata: resources + SCIM-derived groups (unchanged from before) ----
  useEffect(() => {
    if (!tenantId) return
    // Pagination params route this to the admin-facing ListByTenantPage query,
    // which (unlike the bare, catalog-shared ListByTenant) includes resources
    // hidden from the end-user catalog — this page needs to resolve policy
    // resource names/IDs regardless of catalog visibility.
    apiGet<PagedResponse<Resource>>(`/api/v1/tenants/${tenantId}/resources?limit=200`)
      .then((d) => setResources(d?.items ?? []))
      .catch(() => {})

    apiGet<{
      source: string
      items: { claim_value: string; display_name: string; idp_id: string | null; idp_name: string }[]
    }>(`/api/v1/tenants/${tenantId}/groups`)
      .then((d) => {
        const items = d?.items ?? []
        setAvailableGroups(
          items.map((g) => ({
            claim_value: g.claim_value,
            label: `${g.claim_value} (${g.idp_name})`,
          })),
        )
      })
      .catch(() => setAvailableGroups([]))

    apiGet<{ mode_flip_banner_visible: boolean }>(`/api/v1/tenants/${tenantId}/directory/status`)
      .then((s) => setDirBanner(!!s?.mode_flip_banner_visible))
      .catch(() => {})
  }, [tenantId])

  // ---- default facet selection once metadata loads ----
  useEffect(() => {
    if (mode === 'group' && !activeGroup && availableGroups.length > 0) {
      setActiveGroup(availableGroups[0].claim_value)
    }
  }, [mode, availableGroups, activeGroup])

  useEffect(() => {
    if (mode === 'resource' && !activeResource && resources.length > 0) {
      setActiveResource(resources[0].id)
    }
  }, [mode, resources, activeResource])

  // ---- facet counts: one small (limit=1) request per facet, parallelized —
  // this scales with the number of groups/resources, not the number of
  // policies, since it only ever reads the `total` field off each response. ----
  useEffect(() => {
    if (!tenantId) return
    if (mode === 'group') {
      if (availableGroups.length === 0) return
      Promise.all([
        ...availableGroups.map((g) =>
          apiGet<PagedResponse<Policy>>(`/api/v1/tenants/${tenantId}/policies?${qs({ group: g.claim_value, limit: 1 })}`)
            .then((r) => [g.claim_value, r.total] as const)
            .catch(() => [g.claim_value, 0] as const),
        ),
        apiGet<PagedResponse<Policy>>(`/api/v1/tenants/${tenantId}/policies?${qs({ no_group: 'true', limit: 1 })}`)
          .then((r) => [DIRECT_GROUP_KEY, r.total] as const)
          .catch(() => [DIRECT_GROUP_KEY, 0] as const),
      ]).then((entries) => setFacetCounts(Object.fromEntries(entries)))
    } else if (mode === 'resource') {
      if (resources.length === 0) return
      Promise.all(
        resources.map((r) =>
          apiGet<PagedResponse<Policy>>(`/api/v1/tenants/${tenantId}/policies?${qs({ resource_id: r.id, limit: 1 })}`)
            .then((resp) => [r.id, resp.total] as const)
            .catch(() => [r.id, 0] as const),
        ),
      ).then((entries) => setFacetCounts(Object.fromEntries(entries)))
    }
  }, [tenantId, mode, availableGroups, resources])

  // ---- reset to page 1 whenever the facet/mode changes ----
  useEffect(() => {
    setPage(1)
  }, [mode, activeGroup, activeResource])

  const loadList = useCallback(() => {
    if (!tenantId) return
    if (mode === 'group' && !activeGroup) return
    if (mode === 'resource' && !activeResource) return

    setListLoading(true)
    setListError(null)
    const params: Record<string, string | number> = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }
    if (mode === 'resource') params.resource_id = activeResource
    if (mode === 'group') {
      if (activeGroup === DIRECT_GROUP_KEY) params.no_group = 'true'
      else params.group = activeGroup
    }
    apiGet<PagedResponse<Policy>>(`/api/v1/tenants/${tenantId}/policies?${qs(params)}`)
      // Backend's Items is a nil Go slice (serializes to JSON null, not []) when
      // zero rows match, and allowed_subjects/allowed_groups are similarly
      // omitted per-policy when empty — normalize both here so every downstream
      // consumer (search filter, PolicyRow) can assume real arrays, never null/undefined.
      .then((resp) =>
        setListResp({
          ...resp,
          items: (resp.items ?? []).map((p) => ({
            ...p,
            allowed_subjects: p.allowed_subjects ?? [],
            allowed_groups: p.allowed_groups ?? [],
          })),
        }),
      )
      .catch((e: any) => setListError(e.message))
      .finally(() => setListLoading(false))
  }, [tenantId, mode, activeGroup, activeResource, page])

  useEffect(() => {
    loadList()
  }, [loadList])

  // Client-side text filter over the currently loaded page only — the
  // backend's ?group=/?resource_id=/?subject= filters are exact-match, not
  // name search, so free-text search can't be pushed down without a new
  // backend text-search path. Facet-scoped pages stay small regardless of
  // total tenant policy count, so this stays honest in Group/Resource mode;
  // "All Policies" mode calls this out explicitly when it clips results.
  const displayedItems = useMemo(() => {
    const items = listResp.items
    if (!query.trim()) return items
    const q = query.trim().toLowerCase()
    return items.filter((p) => {
      const res = p.resource_id ? resourceById[p.resource_id] : undefined
      const tg = p.tag_group_id ? tagGroupById[p.tag_group_id]?.name ?? '' : ''
      const haystack = [res?.name ?? '', tg, ...p.allowed_groups.map(groupLabel), ...p.allowed_subjects]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [listResp.items, query, resourceById, tagGroupById, groupLabel])

  const filteredGroupFacets = useMemo(() => {
    const q = query.trim().toLowerCase()
    const base = availableGroups.filter((g) => !q || g.label.toLowerCase().includes(q) || g.claim_value.toLowerCase().includes(q))
    return base
  }, [availableGroups, query])

  const filteredResourceFacets = useMemo(() => {
    const q = query.trim().toLowerCase()
    return resources.filter((r) => !q || r.name.toLowerCase().includes(q))
  }, [resources, query])

  const searchClipsResults = query.trim().length > 0 && listResp.total > listResp.items.length

  const openCreate = () => {
    setEditingPolicy(null)
    setShowForm(true)
  }

  const openEdit = (pol: Policy) => {
    setEditingPolicy(pol)
    setShowForm(true)
  }

  const afterMutation = () => {
    loadList()
  }
  // Facet counts don't depend on availableGroups/resources changing after a
  // create/delete (those lists are unaffected), so the count effect above
  // never re-fires on its own — bump this to force a recount.
  const [mutationTick, setMutationTick] = useState(0)
  useEffect(() => {
    if (mutationTick === 0) return
    if (mode === 'group' && availableGroups.length > 0) {
      Promise.all([
        ...availableGroups.map((g) =>
          apiGet<PagedResponse<Policy>>(`/api/v1/tenants/${tenantId}/policies?${qs({ group: g.claim_value, limit: 1 })}`)
            .then((r) => [g.claim_value, r.total] as const)
            .catch(() => [g.claim_value, 0] as const),
        ),
        apiGet<PagedResponse<Policy>>(`/api/v1/tenants/${tenantId}/policies?${qs({ no_group: 'true', limit: 1 })}`)
          .then((r) => [DIRECT_GROUP_KEY, r.total] as const)
          .catch(() => [DIRECT_GROUP_KEY, 0] as const),
      ]).then((entries) => setFacetCounts(Object.fromEntries(entries)))
    } else if (mode === 'resource' && resources.length > 0) {
      Promise.all(
        resources.map((r) =>
          apiGet<PagedResponse<Policy>>(`/api/v1/tenants/${tenantId}/policies?${qs({ resource_id: r.id, limit: 1 })}`)
            .then((resp) => [r.id, resp.total] as const)
            .catch(() => [r.id, 0] as const),
        ),
      ).then((entries) => setFacetCounts(Object.fromEntries(entries)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationTick])

  const handleDelete = async (pol: Policy) => {
    if (!confirm('Delete this policy?')) return
    try {
      const url = pol.tag_group_id
        ? `/api/v1/tenants/${tenantId}/policies/${pol.id}`
        : `/api/v1/tenants/${tenantId}/resources/${pol.resource_id}/policies/${pol.id}`
      await apiDelete(url)
      toast({ title: 'Policy deleted', variant: 'success' })
      afterMutation()
      setMutationTick((t) => t + 1)
    } catch (e: any) {
      toast({ title: 'Delete failed', description: e.message, variant: 'danger' })
    }
  }

  const activeGroupMeta = availableGroups.find((g) => g.claim_value === activeGroup)
  const activeResourceMeta = resourceById[activeResource]

  return (
    <div>
      <PageHeader
        title="Policies"
        description="Define and manage access policies across your resources."
        actions={<Button onClick={openCreate}>+ New Policy</Button>}
      />

      {dirBanner && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-border bg-surface-muted px-4 py-3 text-sm">
          <p>
            Tenant is in <strong>SCIM directory mode</strong>; Authentik live lookup is off.
            Groups from other IdPs appear only after those IdPs are SCIM-onboarded.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                await apiPost(`/api/v1/tenants/${tenantId}/directory/mode-flip-banner/dismiss`, {})
                setDirBanner(false)
              } catch {
                /* ignore */
              }
            }}
          >
            Dismiss
          </Button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          className="min-w-[240px] flex-1"
          placeholder="Search resources, groups, or subjects"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['group', 'resource', 'all'] as Mode[]).map((m, i) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-3.5 py-1.5 text-sm font-medium transition-colors ${i > 0 ? 'border-l border-border' : ''} ${
                mode === m ? 'bg-primary/10 text-primary' : 'bg-surface text-foreground hover:bg-surface-muted'
              }`}
            >
              {m === 'group' ? 'By Group' : m === 'resource' ? 'By Resource' : 'All Policies'}
            </button>
          ))}
        </div>
      </div>

      {mode === 'all' ? (
        <div className="rounded-xl border border-border bg-surface">
          {listLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : listError ? (
            <div className="p-6 text-sm text-danger">{listError}</div>
          ) : listResp.items.length === 0 ? (
            <EmptyState title="No policy" description="Default-deny is in effect tenant-wide." actionLabel="Add Policy" onAction={openCreate} />
          ) : (
            <>
              <DataTable>
                <THead>
                  <Tr>
                    <Th>Resource</Th>
                    <Th>Groups / Subjects</Th>
                    <Th>Constraints</Th>
                    <Th>Effect</Th>
                    <Th className="text-right">Actions</Th>
                  </Tr>
                </THead>
                <tbody>
                  {displayedItems.map((pol) => (
                    <PolicyRow key={pol.id} pol={pol} resourceById={resourceById} tagGroupById={tagGroupById} groupLabel={groupLabel} onEdit={openEdit} onDelete={handleDelete} />
                  ))}
                </tbody>
              </DataTable>
              {searchClipsResults && (
                <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
                  This search only covers the {listResp.items.length} rows loaded on this page, not all{' '}
                  {listResp.total} policies. Turn the page, or narrow by group or resource, for full coverage.
                </p>
              )}
              <div className="border-t border-border px-4 py-3">
                <Pagination page={listResp.page} pageSize={PAGE_SIZE} total={listResp.total} onPageChange={setPage} />
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[240px_1fr] overflow-hidden rounded-xl border border-border bg-surface" style={{ minHeight: 440 }}>
          <div className="overflow-y-auto border-r border-border bg-surface-muted p-2">
            <div className="flex items-center justify-between px-2.5 pb-2 pt-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              <span title={mode === 'group' ? 'Groups come from your connected identity provider. Grants to a person with no group show under Unmapped subjects.' : undefined}>
                {mode === 'group' ? 'Group' : 'Resource'}
              </span>
              <span>Policies</span>
            </div>
            {mode === 'group' &&
              [...filteredGroupFacets.map((g) => ({ key: g.claim_value, name: g.label, sub: g.claim_value })), { key: DIRECT_GROUP_KEY, name: 'Unmapped subjects', sub: 'No group binding' }].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setActiveGroup(f.key)}
                  className={`mb-0.5 flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${
                    activeGroup === f.key ? 'border border-border bg-surface font-semibold text-primary shadow-sm' : 'hover:bg-surface'
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">
                    <span className="block truncate">{f.name}</span>
                    <span className="block truncate text-[11px] font-normal text-muted-foreground">{f.sub}</span>
                  </span>
                  <span className="shrink-0 rounded-md border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {facetCounts[f.key] ?? '-'}
                  </span>
                </button>
              ))}
            {mode === 'resource' &&
              filteredResourceFacets.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setActiveResource(r.id)}
                  className={`mb-0.5 flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${
                    activeResource === r.id ? 'border border-border bg-surface font-semibold text-primary shadow-sm' : 'hover:bg-surface'
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">
                    <span className="block truncate">{r.name}</span>
                    <span className="block truncate text-[11px] font-normal text-muted-foreground">{r.type.toUpperCase()}</span>
                  </span>
                  <span className="shrink-0 rounded-md border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {facetCounts[r.id] ?? '-'}
                  </span>
                </button>
              ))}
          </div>

          <div className="flex min-w-0 flex-col">
            <div className="flex items-baseline justify-between gap-3 border-b border-border px-5 py-4">
              <h2 className="text-sm font-bold">
                {mode === 'group'
                  ? activeGroup === DIRECT_GROUP_KEY
                    ? 'Unmapped subjects'
                    : activeGroupMeta?.label ?? '-'
                  : activeResourceMeta?.name ?? '-'}
              </h2>
              <span className="text-xs text-muted-foreground">
                {mode === 'group'
                  ? activeGroup === DIRECT_GROUP_KEY
                    ? 'Named-subject grants with no group binding'
                    : activeGroupMeta?.claim_value
                  : activeResourceMeta?.type?.toUpperCase()}
              </span>
            </div>

            {listLoading ? (
              <Skeleton className="m-4 h-40" />
            ) : listError ? (
              <div className="p-6 text-sm text-danger">{listError}</div>
            ) : displayedItems.length === 0 ? (
              <EmptyState
                title="No policy"
                description="Default-deny is in effect."
                actionLabel="Add Policy"
                onAction={openCreate}
              />
            ) : (
              <>
                <DataTable>
                  <THead>
                    <Tr>
                      <Th>{mode === 'group' ? 'Resource' : 'Groups / Subjects'}</Th>
                      {mode === 'group' && <Th>Other grants</Th>}
                      <Th>Constraints</Th>
                      <Th>Effect</Th>
                      <Th className="text-right">Actions</Th>
                    </Tr>
                  </THead>
                  <tbody>
                    {displayedItems.map((pol) => (
                      <PolicyRow
                        key={pol.id}
                        pol={pol}
                        resourceById={resourceById}
                        tagGroupById={tagGroupById}
                        groupLabel={groupLabel}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        hideGroup={mode === 'group' ? activeGroup : undefined}
                        axis={mode}
                      />
                    ))}
                  </tbody>
                </DataTable>
                {listResp.total > PAGE_SIZE && (
                  <div className="border-t border-border px-4 py-3">
                    <Pagination page={listResp.page} pageSize={PAGE_SIZE} total={listResp.total} onPageChange={setPage} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <CreatePolicyModal
        tenantId={tenantId}
        resources={resources}
        open={showForm}
        onClose={() => setShowForm(false)}
        editingPolicy={editingPolicy}
        initialResourceId={mode === 'resource' ? activeResource : undefined}
        onSaved={() => {
          afterMutation()
          setMutationTick((t) => t + 1)
        }}
      />
    </div>
  )
}

function PolicyRow({
  pol,
  resourceById,
  tagGroupById,
  groupLabel,
  onEdit,
  onDelete,
  hideGroup,
  axis,
}: {
  pol: Policy
  resourceById: Record<string, Resource>
  tagGroupById: Record<string, { name: string }>
  groupLabel: (claim: string) => string
  onEdit: (pol: Policy) => void
  onDelete: (pol: Policy) => void
  hideGroup?: string
  axis?: Mode
}) {
  const res = pol.resource_id ? resourceById[pol.resource_id] : undefined
  const tg = pol.tag_group_id ? tagGroupById[pol.tag_group_id] : undefined
  const groupChips = pol.allowed_groups.filter((g) => g !== hideGroup).map(groupLabel)
  const subjectChips = pol.allowed_subjects
  const chips = [...groupChips, ...subjectChips]

  return (
    <Tr>
      {axis !== 'resource' && (
        <Td className="text-sm">
          <div>
            {pol.tag_group_id ? (
              <>
                <div className="text-xs text-muted-foreground">Resource group</div>
                <div className="font-medium text-foreground">{tg?.name ?? pol.tag_group_id}</div>
              </>
            ) : (
              <>
                <div className="font-medium text-foreground">{res?.name ?? pol.resource_id}</div>
                <div className="text-xs text-muted-foreground">{res?.type?.toUpperCase()}</div>
              </>
            )}
          </div>
        </Td>
      )}
      {axis === 'group' && (
        <Td className="text-sm">
          {chips.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {chips.map((c) => (
                <Badge key={c}>{c}</Badge>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </Td>
      )}
      {axis !== 'group' && (
        <Td className="text-sm">
          {pol.allowed_groups.length > 0 || pol.allowed_subjects.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {pol.allowed_groups.map((g) => (
                <Badge key={g}>{groupLabel(g)}</Badge>
              ))}
              {pol.allowed_subjects.map((s) => (
                <Badge key={s} className="font-mono">
                  {s}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </Td>
      )}
      <Td className="font-mono text-sm text-muted-foreground">{constraintLabel(pol.constraints)}</Td>
      <Td>
        <Badge variant={pol.effect === 'deny' ? 'danger' : 'success'}>
          {pol.effect === 'deny' ? 'DENY' : 'ALLOW'}
        </Badge>
      </Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(pol)}>
            Edit
          </Button>
          <Button variant="ghost" size="sm" className="text-danger" onClick={() => onDelete(pol)}>
            Delete
          </Button>
        </div>
      </Td>
    </Tr>
  )
}
